import { MapPin, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

interface LocationButtonProps {
  onLocationDetected: (lat: number, lon: number) => void
}

export default function LocationButton({ onLocationDetected }: LocationButtonProps) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const detectLocation = () => {
    if (!('geolocation' in navigator)) {
      setError(t('errors.geolocationNotSupported'));
      return;
    }

    setIsLoading(true);
    setError(null);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        onLocationDetected(position.coords.latitude, position.coords.longitude);
        setIsLoading(false);
      },
      (error) => {
        setIsLoading(false);
        switch (error.code) {
          case error.PERMISSION_DENIED:
            setError(t('errors.locationDenied'));
            break;
          case error.POSITION_UNAVAILABLE:
            setError(t('errors.locationUnavailable'));
            break;
          case error.TIMEOUT:
            setError(t('errors.locationTimeout'));
            break;
          default:
            setError(t('errors.generic'));
        }
        setTimeout(() => setError(null), 5000);
      },
      {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0,
      },
    );
  };

  return (
    <div className="flex flex-col items-end gap-2">
      <motion.button
        onClick={detectLocation}
        disabled={isLoading}
        className="px-4 py-2 rounded-lg bg-primary hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed text-white transition-colors flex items-center gap-2 shadow-lg"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        {isLoading ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            {t('nav.detectingLocation')}
          </>
        ) : (
          <>
            <MapPin className="w-5 h-5" />
            {t('nav.myLocation')}
          </>
        )}
      </motion.button>
      
      {error && (
        <motion.p
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          className="text-sm text-danger bg-danger/10 px-3 py-1 rounded-md border border-danger/30"
        >
          {error}
        </motion.p>
      )}
    </div>
  );
}
